/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { FormGroup, Label, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Button } from 'reactstrap';
import { UnitTypes, getUnitsForType, getCurrencyList } from '../../../utils/units';
import { BasicAlert } from '../../alert/BasicAlert';
import { blueprintDefaultUnitConfig, blueprintDefaultUnitConfigSgx, UnitConfig } from '../../../model/surveyData';
import { AppFlavour, getDefaultConfig } from '../../../config/app-config';
import { InfoIcon } from '../../common/InfoIcon';
import SimpleTooltip from '../../simple-tooltip';

interface UnitOption {
  abbr: string;
  measure?: string;
  plural?: string;
  singular: string;
  system?: string;
}
interface SurveyUnitsAndCurrencyFieldRowProps {
  code: keyof UnitConfig;
  options: UnitOption[];
  disabled?: boolean;
  canEnable?: boolean;
  unitConfig: Partial<UnitConfig>;
  dropdownOpen: string;
  defaultUnitConfig: UnitConfig;
  toggleDropdownOpen: (code: string) => void;
  handleDropdownSelect: (code: string, abbr: string) => void;
  tooltip?: string;
  resetChangedUnit: (key: string) => void
}

type UnitOptionType<T = { title: string }> = {
  [key in keyof UnitConfig]: { tooltip?: string } & T;
};
const SurveyUnitsAndCurrencyText: UnitOptionType = {
  area: {
    title: 'Area',
  },
  length: {
    title: 'Length',
  },
  mass: {
    title: 'Mass',
  },
  volume: {
    title: 'Volume',
  },
  time: {
    title: 'Time',
  },
  energy: {
    title: 'Energy',
  },
  co2Emissions: {
    title: 'CO2 equivalent',
  },
  numberScale: {
    title: 'Financial',
  },
  currency: {
    title: 'Currency',
    tooltip: 'Default currency type set during onboarding, and cannot be updated in life, as G17Eco does not support currency conversion. Please contact us if you have further questions.',
  },
};

const FieldRow = ({
  dropdownOpen,
  toggleDropdownOpen,
  handleDropdownSelect,
  defaultUnitConfig,
  code,
  unitConfig,
  options,
  disabled = false,
  canEnable = false,
  tooltip,
  resetChangedUnit
}: SurveyUnitsAndCurrencyFieldRowProps) => {

  const [isDisabled, setIsDisabled] = useState(disabled)

  if (options.length === 0) {
    return null;
  }

  const rowItem = (name: string, unit: string) => {
    return (
      <div className='row w-100'>
        <div className='col-8'>{name}</div>
        <div className='col-4'>{unit}</div>
      </div>
    );
  };

  const currentSelected = unitConfig[code] ? unitConfig[code] : defaultUnitConfig[code];
  const currentOption = options.find((option: UnitOption) => option.abbr === currentSelected);
  const fieldTitle = SurveyUnitsAndCurrencyText[code].title;
  const fieldTooltip = tooltip || SurveyUnitsAndCurrencyText[code].tooltip;
  const enableButton = <SimpleTooltip text={`(Staff Only) Unlock ${fieldTitle} configuration change, this has a high impact on survey data`}>
    <Button onClick={() => {
      if (!isDisabled) {
        resetChangedUnit(code)
      }
      return !isDisabled || window.confirm(`Are you sure you want to enable ${fieldTitle} configuration change ?`) ? setIsDisabled(!isDisabled) : null
    }
    } size='sm' className='ml-2'><i className={'text-ThemeIconSecondary fas ' + (isDisabled ? 'fa-lock' : 'fa-unlock')} /></Button>
    </SimpleTooltip>

  return (
    <FormGroup className='mb-2'>
      <div className='row'>
        <div className='col-3 align-self-center'>
          <Label className='col-form-label mb-0' for={code}>
            {fieldTitle}
            {isDisabled ? ' (Disabled)' : null}
          </Label>
          {fieldTooltip ? <InfoIcon text={fieldTooltip} className='info-icon' /> : null}

          {canEnable ? enableButton : null}
        </div>
        <div className='col-9'>
          <Dropdown isOpen={dropdownOpen === code} toggle={(e: React.MouseEvent) => toggleDropdownOpen(code)}>
            <DropdownToggle
              disabled={isDisabled}
              caret
              outline
              className='text-left w-100 d-inline-flex align-items-center'
            >
              {currentOption && rowItem(currentOption.singular, currentOption.abbr)}
            </DropdownToggle>
            <DropdownMenu className='w-100'>
              {options.map((u: UnitOption) => (
                <DropdownItem
                  onClick={(e) => handleDropdownSelect(code, u.abbr)}
                  active={currentOption && currentOption.abbr === u.abbr}
                  key={u.abbr}
                  className={u.abbr === defaultUnitConfig[code] ? 'strong' : ''}
                  value={u.abbr}
                >
                  {rowItem(u.singular, u.abbr)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
    </FormGroup>
  );
};

type FieldOptions = Partial<UnitOptionType<{ canEnable?: boolean, disabled?: boolean }>>

const defaultFieldOptions: FieldOptions = {
  currency: {
    disabled: true,
  },
};

interface SurveyUnitsProps {
  setUnitConfig?: (unitConfig: any) => void;
  unitConfig?: UnitConfig;
  isCreating?: boolean;
  isDisabled?: boolean;
  additionalUnitConfig?: Partial<UnitConfig>;
  warningText?: JSX.Element;
  fieldOptions?: FieldOptions;
}

export default function SurveyUnitsAndCurrency(props: SurveyUnitsProps) {
  const {
    setUnitConfig = () => {},
    unitConfig = {},
    isCreating,
    isDisabled = false,
    additionalUnitConfig = {},
    warningText,
    fieldOptions = defaultFieldOptions,
  } = props;

  // @TODO @CTL Probably should load from company settings or use location currency
  const defaults =
    getDefaultConfig().flavour === AppFlavour.SINGAPORE ? blueprintDefaultUnitConfigSgx : blueprintDefaultUnitConfig;

  const defaultUnitConfig: UnitConfig = {
    ...defaults,
    ...additionalUnitConfig,
  };

  const resetChangedUnit = (code: string) => {
    setUnitConfig({
      ...unitConfig,
      [code]: defaultUnitConfig[code as keyof UnitConfig]
    });
  }
  const [hasChanged, setChanged] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState('');

  const handleDropdownSelect = (code: string, abbr: string) => {
    setUnitConfig({
      ...unitConfig,
      [code]: abbr,
    });
    setChanged(true);
  };

  const toggleDropdownOpen = (code: string) => {
    setDropdownOpen(dropdownOpen === code ? '' : code);
  };

  const fieldRowProps: Omit<SurveyUnitsAndCurrencyFieldRowProps, 'code' | 'options'> = {
    dropdownOpen,
    toggleDropdownOpen,
    handleDropdownSelect,
    defaultUnitConfig,
    unitConfig,
    resetChangedUnit
  };

  return (
    <>
      <h5>Units & Currency</h5>
      <div className='mt-3'>
        {(Object.keys(SurveyUnitsAndCurrencyText) as Array<keyof UnitConfig>).map((key: keyof UnitConfig) => {
          const props = {
            ...fieldRowProps,
            code: key,
            key,
            tooltip: fieldOptions[key]?.tooltip,
            disabled: isDisabled || fieldOptions[key]?.disabled,
            canEnable: fieldOptions[key]?.canEnable
          };
          switch (key) {
            case 'currency':
              return <FieldRow {...props} options={getCurrencyList()} />;
            case 'numberScale':
              return <FieldRow {...props} options={getUnitsForType(UnitTypes.currency)} />;
            default:
              return <FieldRow {...props} options={getUnitsForType(key)} />;
          }
        })}
      </div>
      {!isCreating && warningText ? (
        <BasicAlert type={'warning'} hide={!hasChanged}>
          <div>{warningText}</div>
        </BasicAlert>
      ) : null}
    </>
  );
}
